import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button, Alert, Spinner, Modal, Form, OverlayTrigger, Tooltip } from 'react-bootstrap';
import toast from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';
import instance from "../../../services/baseService";
import { transformApiData, reportHeaders } from '../BRCHelper';
import DynamicModal from '../../Employees/DynamicModal';
import { FiX, FiFile } from 'react-icons/fi';
import CreateScripModal from './CreateScripModal';


const EnhancedReportsComponent = () => {
    const [selectedItems, setSelectedItems] = useState(new Set());
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [displayedData, setDisplayedData] = useState([]);
    const [periodData, setPeriodData] = useState({ startDate: "", endDate: "", generate_report: null });
    const [hasMore, setHasMore] = useState(false);
    const [page, setPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [initialLoading, setInitialLoading] = useState(true);
    const tableContainerRef = useRef();
    const { client_id, report_id } = useParams();
    const navigate = useNavigate();
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const [showScripModal, setShowScripModal] = useState(false);
    const [searchValue, setSearchValue] = useState("");
    const [selectedSbNos, setSelectedSbNos] = useState([]);
    const loadingRef = useRef(false);
    const PER_PAGE = 50
    // Fetch data from API
    const fetchReportData = async (pageNum = 1, showLoader = true) => {
        if (showLoader) {
            setLoading(true);
            loadingRef.current = true;
        }
        setError('');

        try {
            const payload = {
                page: pageNum,
                perpage: PER_PAGE
            };

            // Add search parameter if search value has 3+ characters
            if (searchValue.length >= 3) {
                payload.q = searchValue;
            }
            const response = await instance.post(
                `/api/v1/reconciliation/view/report/${client_id}/${report_id}`,
                payload
            );

            if (response.data && response.data.type === 'success') {
                const responseData = response.data.data;
                const transformedData = transformApiData(responseData.lists || []);

                if (pageNum === 1) {
                    // First page - replace all data
                    setDisplayedData(transformedData);
                } else {
                    // Subsequent pages - append data
                    setDisplayedData(prev => [...prev, ...transformedData]);
                }
                setPeriodData({
                    startDate: responseData?.uploaded_file?.start_date,
                    endDate: responseData?.uploaded_file?.end_date,
                    generate_report: responseData?.uploaded_file?.generate_report,
                });
                setTotalCount(responseData.total_count || 0);
                setPage(pageNum);
                setHasMore(transformedData.length === PER_PAGE && (displayedData.length + transformedData.length) < (responseData.total_count || 0));

                if (transformedData.length === 0 && pageNum === 1) {
                    setError('No report data found');
                }
            } else {
                throw new Error(response.data?.message || 'Failed to fetch report data');
            }

        } catch (err) {
            console.error('Fetch Report Data Error:', err);

            if (err.response) {
                const { status, data } = err.response;
                if (status === 404) {
                    setError('Report not found. Please check if the data exists.');
                } else if (status === 403) {
                    setError('Access denied. You don\'t have permission to view this report.');
                } else if (status >= 500) {
                    setError('Server error occurred. Please try again later.');
                } else {
                    setError(data?.message || 'Failed to fetch report data. Please try again.');
                }
            } else if (err.request) {
                setError('Network error. Please check your connection and try again.');
            } else {
                setError(err.message || 'Failed to fetch report data. Please try again.');
            }

            if (pageNum === 1) {
                setDisplayedData([]);
                setTotalCount(0);
            }
        } finally {
            setLoading(false);
            loadingRef.current = false; 
            setInitialLoading(false);
        }
    };

    // Debounced search effect
    useEffect(() => {
        // Only search if we have at least 3 characters
        if (searchValue.length >= 3) {
            // Set a timeout to delay the API call
            const timeoutId = setTimeout(() => {
                console.log('Searching for:', searchValue);
                fetchReportData(1, true); // Reset to page 1 with loader
            }, 500); // Wait 500ms after user stops typing

            // Cleanup: cancel the timeout if searchValue changes again
            return () => clearTimeout(timeoutId);
        } else if (searchValue.length === 0) {
            // If search is cleared, fetch all data
            fetchReportData(1, true);
        }
    }, [searchValue]);

    // Load more data for pagination
    const loadMoreData = useCallback(() => {
        if (hasMore && !loadingRef.current && !initialLoading) {
            fetchReportData(page + 1, true); // true = show loader
        }
    }, [hasMore, initialLoading, page]);

    // Load initial data
    useEffect(() => {
        if (client_id && report_id) {
            fetchReportData(1);
        } else {
            setError('Client ID and Upload ID are required to fetch report data.');
            setInitialLoading(false);
        }
    }, [client_id, report_id]);

    // Intersection Observer for lazy loading
    useEffect(() => {
        if (initialLoading) return;

        const tableContainer = tableContainerRef.current;
        if (!tableContainer) return;

        const handleScroll = () => {
            const { scrollTop, scrollHeight, clientHeight } = tableContainer;

            // Check if scrolled to bottom (within 10px threshold)
            const isAtBottom = scrollHeight - scrollTop - clientHeight <= 10;

            if (isAtBottom && hasMore && !loadingRef.current && !initialLoading) {
                console.log('Loading more data...');
                loadMoreData();
            }
        };

        tableContainer.addEventListener('scroll', handleScroll, { passive: true });

        return () => {
            tableContainer.removeEventListener('scroll', handleScroll);
        };
    }, [hasMore, initialLoading, loadMoreData]);

    // Handle individual checkbox
    const handleSelectItem = (id, checked, rowData) => {
        console.log("rowData:",rowData)
        const newSelectedItems = new Set(selectedItems);
        let newSelectedSbNos = [...selectedSbNos];
        if (checked) {
            // Get the port code from current or first selected item
            const firstSelectedRow = displayedData.find(item => selectedItems.has(item.id));
            const firstSelectedPortCode = firstSelectedRow ? firstSelectedRow.portCode1 : rowData.portCode1;
            console.log("firstSelectedRow:",firstSelectedRow)
            console.log("firstSelectedPortCode:",firstSelectedPortCode)
            // Check if current item's port code matches
            if (rowData.portCode1 !== firstSelectedPortCode) {
                toast.error(`Cannot select items with different port codes. All selected items must have port code: ${firstSelectedPortCode}`);
                return;
            }
            newSelectedItems.add(id);
            newSelectedSbNos.push(rowData.sbNo1); // Store only sb_no
        } else {
            newSelectedItems.delete(id);
            newSelectedSbNos = newSelectedSbNos.filter(sbNo => sbNo !== rowData.sbNo1);
        }

        setSelectedItems(newSelectedItems);
        setSelectedSbNos(newSelectedSbNos);
    };
    const handleProcessSubsidy = async () => {
        setLoading(true);
        setError('');
        try {
            // Call API to get presigned URL
            // const response = await instance.get(
            //     `/api/v1/reconciliation/process-subsidy/${client_id}/${report_id}`
            // );

            // if (response.data.type === 'success') {
            //     navigate(`/port/report/status/${client_id}/${report_id}`)
            //     // toast.success('Report generated and downloaded successfully');
            // } else {
            //     toast.error('Error While processing');
            //     throw new Error('Invalid response from server');
            // }
            navigate(`/port/report/status/${client_id}/${report_id}`)

        } catch (err) {
            console.error('Error processing:', err);
            const errorMessage = err.response?.data?.message || 'Failed to processing. Please try again.';
            setError(errorMessage);
            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    // Handle generate report
    const handleGenerateReport = () => {
        if (periodData.generate_report) {
            handleConfirmGenerateReport()
            return;
        }
        setShowConfirmationModal(true);
    };

    // New: Actual report generation after confirmation
    const handleConfirmGenerateReport = async () => {
        setShowConfirmationModal(false);
        setLoading(true);
        setError('');

        try {
            // Call API to get presigned URL
            const response = await instance.get(
                `/api/v1/reconciliation/generate/report/${client_id}/${report_id}`
            );

            if (response.data.type === 'success' && response?.data?.data?.presigned_url) {
                const presignedUrl = response.data.data.presigned_url;

                // Create a temporary anchor element to trigger download
                const link = document.createElement('a');
                link.href = presignedUrl;

                // Extract filename from URL or use default name
                const urlObj = new URL(presignedUrl);
                const filename = 'reconciliation_report.xlsx';

                link.download = filename;
                // link.target = '_blank';

                // Append to document, click, and remove
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                setPeriodData(prev => ({ ...prev, generate_report: 1 }));
                toast.success('Report generated and downloaded successfully');
            } else {
                throw new Error('Invalid response from server');
            }

        } catch (err) {
            console.error('Error generating report:', err);
            const errorMessage = err.response?.data?.message || 'Failed to generate report. Please try again.';
            setError(errorMessage);
            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    // Handle modal cancel
    const handleCancelGenerateReport = () => {
        setShowConfirmationModal(false);
    };

    const updateDisplayedDataAfterMove = (updatedIds) => {
        setDisplayedData(prevData =>
            prevData.map(item => {
                if (updatedIds.includes(item.id)) {
                    if (item.from_previous === 1) {
                        return {
                            ...item,
                            status: "MATCHED",
                            remarks: "Previously Matched Data"
                        };
                    } else {
                        return {
                            ...item,
                            status: "MATCHED",
                            remarks: "Matched Data"
                        };
                    }
                }
                return item;
            })
        );
    };
    // Handle move action
    const handleMove = async () => {
        if (selectedItems.size === 0) {
            setError('Please select at least one item to move');
            setTimeout(() => setError(''), 5000);
            return;
        }

        setLoading(true);
        setError('');

        try {
            // Convert selectedItems Set to array
            const idsArray = Array.from(selectedItems);

            // Make API call to move items
            const response = await instance.post(
                `/api/v1/reconciliation/almost/move/${client_id}/${report_id}`,
                {
                    ids: idsArray
                }
            );

            if (response.data.type === 'success') {
                updateDisplayedDataAfterMove(response?.data?.data?.updated_ids || [])
                const message = response.data.message || `${selectedItems.size} item(s) moved successfully`;
                toast.success(message);
                setSelectedItems(new Set());
                setSelectedSbNos([]);
            } else {
                throw new Error(response.data.message || 'Failed to move items');
            }

        } catch (err) {
            console.error('Error moving items:', err);
            const errorMessage = err.response?.data?.message || 'Failed to move items. Please try again.';
            setError(errorMessage);
            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleClearSelections = () => {
        setSelectedItems(new Set());
        setSelectedSbNos([]);
    };

    const formatCellValue = (value, column) => {
        if (value === null || value === undefined) return '';

        // Format amounts with currency
        if (column.key.includes('Amount') || column.key.includes('Diff')) {
            if (typeof value === 'number') {
                return new Intl.NumberFormat('en-IN', {
                    style: 'currency',
                    currency: 'INR',
                    maximumFractionDigits: 0
                }).format(value);
            }
        }

        // Format dates
        if (column.key.includes('Date')) {
            if (value) {
                return new Date(value).toLocaleDateString('en-IN');
            }
        }

        // Format numbers
        if (typeof value === 'number') {
            return value.toLocaleString('en-IN');
        }

        return String(value);
    };

    const getCellStyle = (value, column, row) => {
        let style = {
            padding: '8px 12px',
            border: '1px solid #e5e7eb',
            backgroundColor: 'white',
            textAlign: 'left',
            verticalAlign: 'middle',
            minWidth: '100px'
        };

        if (column.key === 'remarks') {
            style.minWidth = '200px';
            style.maxWidth = '300px';
            style.whiteSpace = 'normal';
            style.wordWrap = 'break-word';
            style.lineHeight = '1.4';
        }
        // Section-based styling
        if (column.section === 'statement3') {
            style.backgroundColor = '#f0f9ff'; // Light blue
        } else if (column.section === 'icegate') {
            style.backgroundColor = '#f0fdf4'; // Light green
        } else if (column.section === 'difference') {
            style.backgroundColor = '#fef2f2'; // Light red
        } else if (column.section === 'info') {
            style.backgroundColor = '#f9fafb'; // Gray
            style.fontWeight = 'bold';
        }

        // Amount columns - right align
        if (column.key.includes('Amount') || column.key.includes('Diff')) {
            style.textAlign = 'left';
        }

        // Date columns - center align
        if (column.key.includes('Date')) {
            style.textAlign = 'left';
            style.whiteSpace = 'nowrap';
            style.minWidth = '120px';
        }

        // Status-based row highlighting
        if (row.status === 'MATCHED' && column.key === "remarks") {
            style.backgroundColor = '#45cb85'; // Green
        } else if (row.status === 'ALMOST_MATCH' && column.key === "remarks") {
            style.backgroundColor = '#f3d34a'; // yellow
        } else if (row.status === 'MISSING_IN_ICEGATE' && column.key === "remarks") {
            style.backgroundColor = '#ef4444'; // Red
        } else if (row.status === 'MISSING_IN_STATEMENT3' && column.key === "remarks") {
            style.backgroundColor = '#00a5cf'; // blue
        }

        return style;
    };


    const getColumnHeaderStyle = (column) => {
        let style = {
            padding: '10px 8px',
            textAlign: 'left',
            fontWeight: '600',
            fontSize: '12px',
            border: '1px solid #d1d5db',
            backgroundColor: '#ffffc4ff',
            color: 'black'
        };

        if (column.section === 'statement3') {
            style.backgroundColor = '#dbeafe'; // Light blue
        } else if (column.section === 'icegate') {
            style.backgroundColor = '#dcfce7'; // Light green
        } else if (column.section === 'difference') {
            style.backgroundColor = '#fecaca'; // Light red
        }

        return style;
    };

    const getSectionHeaderStyle = (section) => { // change the order of the function render to remove the color bleeding
        const baseStyle = {
            padding: '12px',
            textAlign: 'center',
            fontWeight: 'bold',
            fontSize: '14px',
            color: 'black',
            borderLeft: '1px solid #e5e7eb',
            borderRight: '1px solid #e5e7eb',
            borderBottom: '1px solid #e5e7eb',
            // borderTop:'1px solid #e5e7eb',
        };

        switch (section.title) {
            case 'Statement 3 Data':
                return { ...baseStyle, backgroundColor: '#F8F9FA' }; // Blue
            case 'ICEGATE data':
                return { ...baseStyle, backgroundColor: '#F8F9FA' }; // Green
            case 'Difference':
                return { ...baseStyle, backgroundColor: '#F8F9FA' }; // Red
            default:
                return { ...baseStyle, backgroundColor: '#F8F9FA' }; // Gray
        }
    };

    const clearMessages = () => {
        setError('');
    };

    const handleSearch = (e) => {
        setSearchValue(e.target.value);
        console.log("Searching Port Code:", e.target.value);
    };

    // Show loading state
    if (initialLoading) {
        return (

            <div className="text-center py-5">
                <Spinner animation="border" role="status" className="mb-3">
                    <span className="visually-hidden">Loading...</span>
                </Spinner>
                <p className="text-gray-600">Loading report data...</p>
            </div>
        );
    }
    return (
        <div className="max-w-full">
            {/* Header */}
            <div className="mb-2">
                <div className="flex justify-between items-center">
                    <div>
                        <h2 className="text-xl px-2 font-bold text-gray-900">Reconciliation Report</h2>
                        <div className="flex items-center justify-center">
                            {/* <h5 className="px-2 font-bold text-gray-900 mb-0">Period:</h5> */}
                            <span className="inline-flex items-center px-3 py-2 bg-green-100 text-green-800 rounded-full fw-bold">
                                {periodData.startDate &&
                                    new Date(periodData.startDate).toLocaleDateString("en-US", {
                                        day: "2-digit",
                                        month: "long",
                                        year: "numeric",
                                    })}
                                {" "} - {" "}
                                {periodData.endDate &&
                                    new Date(periodData.endDate).toLocaleDateString("en-US", {
                                        day: "2-digit",
                                        month: "long",
                                        year: "numeric",
                                    })}
                            </span>
                        </div>
                    </div>
                    <div className="d-flex align-items-center gap-3">
                        <Form.Control
                            type="text"
                            placeholder="Search Port Code or SB No..."
                            value={searchValue}
                            onChange={handleSearch}
                            style={{ width: '250px' }}
                        />
                            <span className="d-inline-block">
                                <Button
                                    variant="primary"
                                    className="no-hover fw-bold d-flex align-items-center justify-content-center"
                                    style={{ width: "150px"}}
                                    // disabled={!periodData.generate_report}
                                    onClick={() => {
                                        if (selectedSbNos.length === 0) {
                                            toast.error('Please select at least one item to create scrip');
                                            return;
                                        }
                                        setShowScripModal(true);
                                    }}
                                >
                                    <FiFile className="me-2" />
                                    Create Scrip
                                </Button>
                            </span>
                    </div>
                </div>
            </div>

            {/* Error Alert */}
            {error && (
                <Alert variant="danger" dismissible onClose={clearMessages} className="mb-3">
                    {error}
                </Alert>
            )}
            {/* {selectedItemsData.length > 0 && periodData.generate_report && (
                <div className="text-sm text-blue-600 font-medium">
                    Selected Port Code: {selectedItemsData[0].portCode1}
                </div>
            )} */}
            {/* Excel-style Table */}
            <div
                ref={tableContainerRef}
                className="overflow-auto"
                style={{ maxHeight: '600px' }}
            >
                <table className="border-collapse" style={{ minWidth: '100%' }}>
                    {/* Section Headers */}
                    <thead>
                        <tr>
                            {reportHeaders.mainSections.map((section, index) => (
                                <th
                                    key={index}
                                    colSpan={section.colspan}
                                    style={getSectionHeaderStyle(section)}
                                    className="sticky top-0 z-10"
                                >
                                    {section.title}
                                </th>
                            ))}
                        </tr>
                        {/* Column Headers */}
                        <tr>
                            <th
                                style={{
                                    ...getColumnHeaderStyle({ section: 'info' }),
                                    position: 'sticky',
                                    top: '35px',
                                    zIndex: 10
                                }}
                            >
                                Select
                            </th>
                            {reportHeaders.columns.map((column, index) => (
                                <th
                                    key={index}
                                    style={{
                                        ...getColumnHeaderStyle(column),
                                        position: 'sticky',
                                        top: '35px',
                                        zIndex: 10
                                    }}
                                >
                                    {column.title}
                                </th>
                            ))}
                        </tr>
                    </thead>

                    <tbody>
                        {displayedData.map((row, rowIndex) => (
                            <tr key={row.id}>
                                <td
                                    style={{
                                        padding: '8px 12px',
                                        border: '1px solid #e5e7eb',
                                        backgroundColor: '#ffffe7ff',
                                        textAlign: 'center',
                                        verticalAlign: 'middle'
                                    }}
                                >
                                    {(
                                        (row.status === 'ALMOST_MATCH') ||
                                        ((row.status === 'MATCHED' || row.status === 'PREVIOUSLY_MATCHED'))
                                    ) ? (
                                        <input
                                            type="checkbox"
                                            checked={selectedItems.has(row.id)}
                                            onChange={(e) => handleSelectItem(row.id, e.target.checked, row)}
                                            disabled={loading}
                                            className="rounded border-gray-300"
                                        />
                                    ) : null}
                                </td>
                                {reportHeaders.columns.map((column, colIndex) => (
                                    <td
                                        key={colIndex}
                                        style={getCellStyle(row[column.key], column, row)}
                                    >
                                        {formatCellValue(row[column.key], column)}
                                    </td>
                                ))}
                            </tr>
                        ))}
                        {hasMore && (
                            <tr>
                                <td colSpan={reportHeaders.columns.length + 1} className="text-center py-4">
                                    {loading && (
                                        <Spinner animation="border" role="status" size="sm" className="me-2">
                                            <span className="visually-hidden">Loading...</span>
                                        </Spinner>
                                    )}
                                    <span className="text-sm text-gray-600">
                                        {loading ? 'Loading more data...' : 'Scroll down for more'}
                                    </span>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Action Section */}
            <div className="mt-6 flex justify-between items-center p-4 bg-gray-50 rounded-lg">
                <div>
                    {selectedItems.size > 0 && (
                        <span className="text-sm text-gray-600">
                            {selectedItems.size} item(s) selected
                        </span>
                    )}
                    <div className="text-xs text-gray-500 mt-1">
                        Showing {displayedData.length} of {totalCount} records
                    </div>
                </div>

                <div className="flex gap-3">
                    <Button
                        id='generate_section_id'
                        variant="primary"
                        onClick={handleProcessSubsidy}
                        
                        disabled={loading}
                        className="d-flex align-items-center fw-bold no-hover"
                    >
                        <svg
                            className="w-4 h-4 mr-2"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                            />
                        </svg>
                        View Process Subsidy
                    </Button>
                    <Button
                        variant='primary'
                        onClick={handleGenerateReport}
                        disabled={loading}
                        className="px-6 py-2 font-medium rounded-lg border"
                    >
                        <div className="flex items-center">
                            <svg
                                className="w-4 h-4 mr-2"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                />
                            </svg>
                            {periodData?.generate_report ? "Download Report" : "Generate Report"}
                        </div>
                    </Button>

                    {/* <Button
                        variant='primary'
                        onClick={handleMove}
                        disabled={loading || selectedItems.size === 0 || periodData?.generate_report}
                        className="px-6 py-2 font-medium rounded-lg"
                    >
                        <div className="flex items-center">
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16l-4-4m0 0l4-4m-4 4h18" />
                            </svg>
                            Move
                        </div>
                    </Button> */}
                </div>
            </div>
            <CreateScripModal
                show={showScripModal}
                onHide={() => {
                    setShowScripModal(false);
                    handleClearSelections();
                }}
                portCode={selectedSbNos.length > 0 ? displayedData.find(item => selectedItems.has(item.id))?.portCode1 : ""}
                sbNoList={selectedSbNos}
            />

            <DynamicModal
                show={showConfirmationModal}
                onHide={handleCancelGenerateReport}
                title="Confirm Report Generation"
                size="md"
                centered={true}
                loading={loading}
                buttons={[
                    {
                        label: "Cancel",
                        variant: "secondary",
                        action: "close"
                    },
                    {
                        label: "Generate",
                        variant: "primary",
                        action: "custom",
                        onClick: handleConfirmGenerateReport,
                        loadingLabel: "Generating...",
                        disabled: loading
                    }
                ]}
                onCancel={handleCancelGenerateReport}
            >
                <div className="text-center py-4">
                    <div className="mb-4">
                        <svg
                            className="mx-auto h-12 w-12 text-warning"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.982 16.5c-.77.833.192 2.5 1.732 2.5z"
                            />
                        </svg>
                    </div>
                    <h4 className="mb-3">Generate Reconciliation Report?</h4>
                    <p className="text-sm text-muted mt-3">
                        After Generating report you can't move any shipping bills.
                    </p>
                </div>
            </DynamicModal>
        </div>
    );
};

export default EnhancedReportsComponent;